import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, ArrowLeft, Send, Sparkles } from "lucide-react";
import VerifiedBadge from "@/components/plyx/VerifiedBadge";
import { GuardedText } from "@/components/GuardedText";
import { toast } from "sonner";

/**
 * Plyx DM Thread — /plyx/dm/:conversationId
 * Polling-based real-time-ish chat. Hits /messages every 5s while focused
 * + marks the conversation as read on mount and after each successful send.
 */
const PlyxDMThread = () => {
  const { conversationId } = useParams();
  const { user } = usePlyxAuth();
  const [partner, setPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  // Smart-reply state (iter-115). Suggestions are fetched on-demand
  // when the viewer taps the ✨ button — never auto-fetched, never
  // auto-translated. Pro-gated server-side.
  const [smartReplies, setSmartReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const scrollerRef = useRef(null);

  const fetchSmartReplies = async () => {
    if (loadingReplies) return;
    if (!user?.is_plyx_pro) {
      toast.error("Smart reply is a Plyx Pro perk", {
        description: "Upgrade to let AI draft replies in your voice.",
        action: { label: "Go Pro", onClick: () => { window.location.href = "/plyx/pro"; } },
      });
      return;
    }
    setLoadingReplies(true);
    try {
      const r = await plyxApi.post("/plyx/ai/suggest-reply", {
        conversation_id: conversationId,
        max_replies: 3,
      });
      setSmartReplies(r.data?.replies || []);
      if (!(r.data?.replies || []).length) toast.error("No suggestions right now");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const code = (detail && typeof detail === "object") ? detail.code : null;
      if (status === 402 || code === "pro_required") {
        toast.error("Smart reply is a Plyx Pro perk", {
          description: "Upgrade to let AI draft replies in your voice.",
          action: { label: "Go Pro", onClick: () => { window.location.href = "/plyx/pro"; } },
        });
      } else {
        toast.error((typeof detail === "string" && detail) || "Couldn't fetch suggestions");
      }
    } finally {
      setLoadingReplies(false);
    }
  };

  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  };

  const markRead = () => {
    plyxApi.post(`/plyx/dm/conversations/${conversationId}/read`).catch(() => {});
  };

  const fetchMessages = async () => {
    try {
      const { data } = await plyxApi.get(`/plyx/dm/conversations/${conversationId}/messages?limit=80`);
      setMessages(data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("This conversation isn't yours.");
    }
  };

  // Bootstrap: hydrate partner via /conversations list (cheap), then load messages.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await plyxApi.get("/plyx/dm/conversations?limit=100");
        const hit = (list.data?.items || []).find((c) => c.conversation_id === conversationId);
        if (alive) setPartner(hit?.partner || null);
        await fetchMessages();
      } catch (e) {
        if (alive) toast.error("Could not open conversation.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Poll for new messages every 30s while focused — fallback only.
  // The primary delivery path is the WebSocket below; polling exists
  // to recover from a dropped socket or stale-tab focus event.
  useEffect(() => {
    const t = setInterval(fetchMessages, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Real-time WebSocket subscription — instant DM delivery + typing
  // indicator. The HTTP fallback (above) only fires if this drops.
  useEffect(() => {
    if (!conversationId || !user) return;
    const token = localStorage.getItem("plyx_token") || "";
    if (!token) return;
    // Derive ws:// from REACT_APP_BACKEND_URL so production + preview
    // both work without further config.
    const httpBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsBase = httpBase.replace(/^http/i, "ws");
    const url = `${wsBase}/api/ws/plyx/dm/${encodeURIComponent(conversationId)}?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "message" && msg.message) {
          // Dedupe: ignore my own echoed sends (already added optimistically).
          setMessages((prev) => {
            if (prev.some((m) => m.message_id === msg.message.message_id)) return prev;
            return [...prev, msg.message];
          });
        }
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => { /* HTTP poll fallback will refresh */ };
    return () => {
      try { ws && ws.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, user]);

  // Auto-scroll + mark read whenever messages change.
  useEffect(() => {
    if (messages.length === 0) return;
    scrollToBottom();
    markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    // Optimistic add.
    const optimistic = {
      message_id: `tmp-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: user.plyx_user_id,
      body: text,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setBody("");
    try {
      await plyxApi.post(`/plyx/dm/conversations/${conversationId}/messages`, { body: text });
      await fetchMessages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send");
      // Roll back optimistic.
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5">
        <div className="text-center">
          <div className="font-semibold">Sign in to read messages.</div>
          <Link to="/plyx/login" className="mt-3 inline-block px-5 py-2 rounded-full bg-white text-white text-sm font-bold">Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0A0A0A] text-white flex flex-col" data-testid="plyx-dm-thread">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <Link to="/plyx/dm" className="p-1.5 rounded-full hover:bg-white/10" data-testid="plyx-dm-back">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="w-9 h-9 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
          {partner?.avatar_url ? (
            <img src={plyxMediaUrl(partner.avatar_url)} alt={partner.handle} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60 font-semibold">
              {(partner?.display_name || partner?.handle || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm inline-flex items-center gap-1">
            @{partner?.handle || "…"}
            <VerifiedBadge user={partner} size={12} />
          </div>
          {partner?.country && <div className="text-[11px] text-white/55">{partner.country}</div>}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-2"
        data-testid="plyx-dm-scroll"
      >
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-white/30" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-white/55 py-10">
            No messages yet. Say something nice 👋
          </div>
        ) : (
          messages.map((m) => {
            const isMine = m.sender_id === user.plyx_user_id;
            return (
              <div
                key={m.message_id}
                className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                data-testid={`plyx-dm-msg-${m.message_id}`}
              >
                <div
                  className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words ${
                    isMine
                      ? "bg-gradient-to-br from-[#FFB42D] to-[#FF2D7A] text-white rounded-br-md"
                      : "bg-white/10 text-white rounded-bl-md"
                  } ${m._pending ? "opacity-60" : ""}`}
                >
                  <GuardedText text={m.body} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Smart-reply suggestion chips (iter-115).
          Rendered ABOVE the composer when replies exist so the viewer
          taps a chip to populate the input — they still hit Send
          themselves, so AI never speaks on their behalf without an
          explicit action. Pro-only. */}
      {smartReplies.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 px-4 pt-2 border-t border-white/10 shrink-0"
          data-testid="plyx-dm-smart-reply-row"
        >
          {smartReplies.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setBody(s); setSmartReplies([]); }}
              data-testid={`plyx-dm-smart-reply-${i}`}
              className="text-xs text-white/90 bg-white/10 hover:bg-white/20 border border-white/15 rounded-full px-3 py-1 max-w-[80%] truncate"
            >
              {s}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSmartReplies([])}
            className="text-[10px] text-white/40 hover:text-white/70 px-1"
          >
            dismiss
          </button>
        </div>
      )}

      {/* Composer */}
      <form onSubmit={send} className="flex items-center gap-2 px-4 py-3 border-t border-white/10 shrink-0">
        <button
          type="button"
          onClick={fetchSmartReplies}
          disabled={loadingReplies || messages.length === 0}
          title="AI smart reply (Plyx Pro)"
          aria-label="Suggest replies with AI"
          data-testid="plyx-dm-smart-reply-trigger"
          className="w-9 h-9 rounded-full flex items-center justify-center text-white/85 hover:text-[#FFB42D] disabled:opacity-30 border border-white/15"
        >
          {loadingReplies ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
        </button>
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
          placeholder="Type a message…"
          data-testid="plyx-dm-input"
          className="flex-1 rounded-full bg-white/10 border border-white/15 px-4 py-2.5 text-sm outline-none focus:border-[#FFB42D] placeholder:text-white/40"
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          data-testid="plyx-dm-send"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          aria-label="Send message"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

export default PlyxDMThread;
